import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { request } from '@/api/apiUtils';
import { tagCategoryId, type textAtomTypeId } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import { calculateOrderBetween } from '@/hooks/optimisticHelpers';
import type { CreateTextAtom, IEntityTag, ITag, ResponseType, TextAtoms } from '@/types';
import { getOptimisticToRealIdMap } from './internal/optimisticIdMap';
import { type CacheableScene, invalidateEditorCaches, optimisticTransform, replaceOptimisticAtom } from './internal/textAtomCacheManager';

// ============================================================================
// Types
// ============================================================================

interface TextAtomSaveConfig {
    textableId: number;
    textableModel: string;
    textAtomTypeId: textAtomTypeId;
    order?: number;
    successCallback?: (result: TextAtoms) => void;
}

interface UpdateTextAtomParams {
    project_id: number;
    id: number;
    content?: string;
    tags?: number[];
    content_json?: string | null;
}

interface CreateTextAtomParams {
    project_id: number;
    data: CreateTextAtom;
    characterData?: { entity_tag: IEntityTag; name: string; id: number; color?: string } | null;
    uiPreviousId?: number | null;
    optimisticId?: number;
}

interface DeleteTextAtomParams {
    project_id: number;
    id: number;
}

// ============================================================================
// Internal Mutations
// ============================================================================

function useUpdateTextAtomInstant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateTextAtomParams): Promise<ResponseType<TextAtoms>> => {
            const endpoint = `projects/${params.project_id}/text-atoms/${params.id}`;
            const data: Partial<Pick<CreateTextAtom, 'content' | 'tags' | 'content_json'>> = {};
            if (params.content !== undefined) data.content = params.content;
            if (params.tags !== undefined) data.tags = params.tags;
            if (params.content_json !== undefined) data.content_json = params.content_json;

            return await request(endpoint, {
                method: 'PATCH',
                body: JSON.stringify(data),
            });
        },
        onMutate: async params => {
            const { rollback } = await optimisticTransform(queryClient, (scene: CacheableScene) => {
                if (!scene.text_atoms || !Array.isArray(scene.text_atoms)) return scene;
                return {
                    ...scene,
                    text_atoms: scene.text_atoms.map((atom: TextAtoms) =>
                        atom.id === params.id
                            ? {
                                  ...atom,
                                  content: params.content ?? atom.content,
                                  content_json: params.content_json ?? atom.content_json,
                              }
                            : atom,
                    ),
                };
            });
            return { rollback };
        },
        onError: (_err, _params, context) => {
            context?.rollback();
        },
        onSettled: () => {
            invalidateEditorCaches(queryClient);
        },
    });
}

function useCreateTextAtomInstant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateTextAtomParams): Promise<ResponseType<TextAtoms>> => {
            const endpoint = `projects/${params.project_id}/text-atoms`;
            return await request(endpoint, {
                method: 'POST',
                body: JSON.stringify(params.data),
            });
        },
        onMutate: async params => {
            const optimisticId = params.optimisticId || Date.now();
            let calculatedOrder = params.data.order || 0;

            const { rollback } = await optimisticTransform(queryClient, (scene: CacheableScene) => {
                if (scene.id !== params.data.textable_id || params.data.textable_model !== 'scene') return scene;

                const textAtoms = scene.text_atoms || [];

                // Find previous and next atoms to calculate order
                let previousAtom: TextAtoms | null = null;
                let nextAtom: TextAtoms | null = null;
                const previousIdForUI = params.uiPreviousId !== undefined ? params.uiPreviousId : params.data.previous_id;

                if (previousIdForUI) {
                    previousAtom = textAtoms.find((atom: TextAtoms) => atom.id === previousIdForUI) || null;
                }
                if (params.data.next_id) {
                    nextAtom = textAtoms.find((atom: TextAtoms) => atom.id === params.data.next_id) || null;
                }
                if (!previousIdForUI && !params.data.next_id && textAtoms.length > 0) {
                    previousAtom = textAtoms[textAtoms.length - 1] ?? null;
                }

                calculatedOrder = calculateOrderBetween(previousAtom, nextAtom);

                // Calculate insertion position
                let insertIndex = textAtoms.length;
                if (previousIdForUI) {
                    const prevIndex = textAtoms.findIndex((atom: TextAtoms) => atom.id === previousIdForUI);
                    if (prevIndex >= 0) insertIndex = prevIndex + 1;
                }

                const simulatedTags: ITag[] = [];
                if (params.data.add_tags && params.data.add_tags.length > 0 && params.characterData) {
                    const character = params.characterData;
                    simulatedTags.push({
                        id: character.entity_tag.id,
                        name: character.name,
                        tag_category_id: tagCategoryId.characterTag,
                        color: character.entity_tag?.color || character.color || '#000000',
                        category: {
                            id: tagCategoryId.characterTag,
                            name: 'Character',
                            target_type: 'character',
                            target_id: character.id,
                        },
                        subject_type: 'text_atom',
                        subject_id: optimisticId,
                        subject_model: 'TextAtom',
                    });
                }

                const optimisticAtom: TextAtoms = {
                    id: optimisticId,
                    content: params.data.content || '',
                    content_json: params.data.content_json || undefined,
                    text_atom_type_id: params.data.text_atom_type_id,
                    tags: simulatedTags,
                    project_id: params.project_id,
                    order: calculatedOrder,
                    textable_id: params.data.textable_id,
                    textable_type: params.data.textable_model,
                };

                const newTextAtoms = [...textAtoms];
                newTextAtoms.splice(insertIndex, 0, optimisticAtom);

                return { ...scene, text_atoms: newTextAtoms };
            });

            // Update the mutation data to include the calculated order
            if (!params.data.order && calculatedOrder > 0) {
                params.data.order = calculatedOrder;
            }

            return { rollback, optimisticId };
        },
        onError: (_err, _params, context) => {
            context?.rollback();
        },
        onSuccess: (result, params, context) => {
            if (result.success) {
                // Map optimistic ID to real ID
                if (context?.optimisticId && result.data.id) {
                    getOptimisticToRealIdMap().set(context.optimisticId, result.data.id);
                }

                // Replace optimistic atom with real server data in all caches
                if (context?.optimisticId) {
                    replaceOptimisticAtom(queryClient, context.optimisticId, result.data, params.data.textable_id);
                }

                invalidateEditorCaches(queryClient);
            }
        },
    });
}

function useDeleteTextAtomInstant() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: DeleteTextAtomParams): Promise<ResponseType<TextAtoms>> => {
            const endpoint = `projects/${params.project_id}/text-atoms/${params.id}`;
            return await request(endpoint, { method: 'DELETE' });
        },
        onMutate: async params => {
            const { rollback } = await optimisticTransform(queryClient, (scene: CacheableScene) => {
                if (!scene.text_atoms || !Array.isArray(scene.text_atoms)) return scene;
                return {
                    ...scene,
                    text_atoms: scene.text_atoms.filter((atom: TextAtoms) => atom.id !== params.id),
                };
            });
            return { rollback };
        },
        onError: (_err, _params, context) => {
            context?.rollback();
        },
        onSettled: () => {
            invalidateEditorCaches(queryClient);
        },
    });
}

// ============================================================================
// Public Hook
// ============================================================================

/**
 * Save a text atom with optimistic cache updates.
 *
 * Handles create (when no existing atom), update, and delete-on-empty routing internally.
 * Returns a `(content, contentJson?) => void` function.
 *
 * This is the 80% hook — most callers just need to save text content.
 */
export const useTextAtomSave = (
    { textableId, textableModel, order, textAtomTypeId, successCallback }: TextAtomSaveConfig,
    textAtom?: TextAtoms,
) => {
    const project_id = useRequiredAppSelector('projectId');
    const updateMutation = useUpdateTextAtomInstant();
    const createMutation = useCreateTextAtomInstant();
    const deleteMutation = useDeleteTextAtomInstant();

    return useCallback(
        (content: string, content_json?: string | null) => {
            if (textAtom) {
                const hasContentChanged = content !== textAtom.content;
                const hasContentJsonChanged = content_json !== textAtom.content_json;

                const isLogline = textAtomTypeId === 12; // scene_logline
                const shouldUpdate = hasContentChanged || hasContentJsonChanged || (isLogline && content.trim().length === 0);

                if (shouldUpdate) {
                    if (content.trim().length === 0 && !content_json && textAtomTypeId !== 12) {
                        // Delete if content is empty (except for loglines)
                        deleteMutation.mutate(
                            { project_id, id: textAtom.id },
                            {
                                onSuccess: result => {
                                    if (result.success && successCallback) successCallback(result.data);
                                },
                            },
                        );
                    } else {
                        // Update atom
                        updateMutation.mutate(
                            {
                                project_id,
                                id: textAtom.id,
                                content: content || '',
                                ...(content_json !== undefined && { content_json }),
                            },
                            {
                                onSuccess: result => {
                                    if (result.success && successCallback) successCallback(result.data);
                                },
                            },
                        );
                    }
                }
            } else if (content.trim().length > 0 || content_json || textAtomTypeId === 12) {
                // Create new atom (including empty loglines)
                const data: CreateTextAtom = {
                    textable_model: textableModel,
                    textable_id: textableId,
                    text_atom_type_id: textAtomTypeId,
                    content,
                    content_json,
                };

                if (typeof order !== 'undefined') data.order = order;

                const optimisticId = Date.now();

                createMutation.mutate(
                    { project_id, data, optimisticId },
                    {
                        onSuccess: result => {
                            if (result.success && successCallback) successCallback(result.data);
                        },
                    },
                );
            }
        },
        [
            textAtom,
            updateMutation,
            createMutation,
            deleteMutation,
            successCallback,
            textAtomTypeId,
            textableModel,
            textableId,
            order,
            project_id,
        ],
    );
};
